import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { videoConfigs, videoTransitions } from '../config/videos';
import { useVideoPreloader } from './useVideoPreloader';
import { useVideoTransitionState } from './useVideoTransitionState';

// Constants for transition timing
const CONTENT_FADE_DURATION = 400;
const VIDEO_TRANSITION_DURATION = 0;

// Global state to track if user has unmuted videos in current session
if (typeof window !== 'undefined') {
    window.STILL_LOVED_VIDEO_UNMUTED = window.STILL_LOVED_VIDEO_UNMUTED || false;
    window.STILL_LOVED_USER_INTERACTED = window.STILL_LOVED_USER_INTERACTED || false;
}

// Global instance tracking
const activeInstance = {
    id: null,
    isDebugEnabled: false
};

const debug = (...args) => {
    if (activeInstance.isDebugEnabled) {
        //console.log('[useVideoTransition]', ...args);
    }
};

export const useVideoTransition = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { preloadVideo: preloadVideoFromHook } = useVideoPreloader();
    const { acquireTransitionLock, releaseTransitionLock, setSeamlessTransition, getTransitionState } = useVideoTransitionState();
    const [contentVisible, setContentVisible] = useState(true);
    const previousRoute = useRef(location.pathname);
    const isInitialMount = useRef(true);
    const pendingNavigation = useRef(null);
    const instanceIdRef = useRef(`video-transition-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    
    // Add state for playing
    const [isPlaying, setIsPlaying] = useState(false);
    
    const [videoState, setVideoState] = useState(() => {
        const normalizedPath = location.pathname.replace(/\/$/, '');
        const currentVideo = videoConfigs.find(v => {
            const normalizedRoute = v.route.replace(/\/$/, '');
            return normalizedRoute.endsWith('/*') 
                ? normalizedPath.startsWith(normalizedRoute.slice(0, -2))
                : normalizedRoute === normalizedPath;
        });
        
        return {
            background: currentVideo?.src || videoConfigs[0]?.src || '',
            transition: '',
            isTransitioning: false,
            shouldReload: true
        };
    });

    // Instance management
    useEffect(() => {
        if (activeInstance.id) {
            debug(`Another instance is already active (${activeInstance.id}). This instance (${instanceIdRef.current}) will not initialize.`);
            return;
        }

        activeInstance.id = instanceIdRef.current;
        debug(`Instance ${instanceIdRef.current} mounted and active.`);

        return () => {
            if (activeInstance.id === instanceIdRef.current) {
                debug(`Instance ${instanceIdRef.current} unmounting and clearing active flag.`);
                activeInstance.id = null;
                releaseTransitionLock(instanceIdRef.current);
            }
        };
    }, [releaseTransitionLock]);

    const preloadVideo = useCallback(async (src) => {
        if (!src) return true;
        
        const { isSeamlessTransition } = getTransitionState();
        if (isSeamlessTransition) {
            debug(`Skipping preload check for seamless transition`);
            return true;
        }

        debug(`Starting preload check for: ${src}`);
        return preloadVideoFromHook(src);
    }, [preloadVideoFromHook, getTransitionState]);

    const performTransition = useCallback(async (newBackground, transition = null) => {
        debug(`Starting transition to ${newBackground}`, transition ? `with transition: ${transition.src}` : '');
        
        // Try to acquire the transition lock
        if (!acquireTransitionLock(instanceIdRef.current)) {
            debug(`Could not acquire transition lock, skipping transition`);
            return;
        }
        
        try {
            // Check if this is a landing to passive screen transition
            const isLandingToPassiveTransition = 
                previousRoute.current === '/' && 
                location.pathname === '/space' && 
                newBackground.includes('/assets/loop.mp4');
                
            if (isLandingToPassiveTransition) {
                debug(`Detected landing to passive transition - using instant transition`);
                setSeamlessTransition(instanceIdRef.current, true);
                
                // Skip content fade for instant transition
                setIsPlaying(true);
                
                setVideoState(prev => ({
                    ...prev,
                    background: newBackground,
                    transition: '',
                    isTransitioning: false,
                    shouldReload: false
                }));
                
                setContentVisible(true);
                return;
            }
            
            // Regular transition logic for all other cases
            const isSameBackground = videoState.background === newBackground;
            
            if (!isSameBackground) {
                const preloadResults = await Promise.all([
                    transition ? preloadVideo(transition.src) : true,
                    preloadVideo(newBackground)
                ]);
                
                const [transitionLoaded, backgroundLoaded] = preloadResults;
                if (!backgroundLoaded) {
                    debug(`Background video failed to preload: ${newBackground}`);
                }
                
                if (transition && !transitionLoaded) {
                    debug(`Transition video failed to preload: ${transition.src}`);
                    transition = null;
                }
            }

            setContentVisible(false);
            await new Promise(resolve => setTimeout(resolve, CONTENT_FADE_DURATION));

            setIsPlaying(true);
            
            if (isSameBackground && !transition) {
                setVideoState(prev => ({
                    ...prev,
                    shouldReload: true
                }));
            } else {
                setVideoState(prev => ({
                    ...prev,
                    transition: transition?.src || '',
                    background: transition ? transition.src : newBackground,
                    isTransitioning: true,
                    shouldReload: true
                }));

                await new Promise(resolve => setTimeout(resolve, VIDEO_TRANSITION_DURATION));

                setVideoState(prev => ({
                    background: newBackground,
                    transition: '',
                    isTransitioning: false,
                    shouldReload: true
                }));
            }

            setContentVisible(true);
        } catch (error) {
            debug(`Transition error:`, error);
            setVideoState(prev => ({
                background: newBackground,
                transition: '',
                isTransitioning: false,
                shouldReload: true
            }));
            setContentVisible(true);
        } finally {
            releaseTransitionLock(instanceIdRef.current);
        }
    }, [videoState, acquireTransitionLock, releaseTransitionLock, setSeamlessTransition, location.pathname, preloadVideo]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (location.pathname === previousRoute.current) return;

        debug(`Route changed from ${previousRoute.current} to ${location.pathname}`);
        
        // Skip if this is a landing to passive screen transition that's already being handled
        const { isTransitionInProgress, isSeamlessTransition } = getTransitionState();
        if (previousRoute.current === '/' && location.pathname === '/space' && isTransitionInProgress && isSeamlessTransition) {
            debug(`Skipping route change handler for landing to passive transition - already being handled`);
            previousRoute.current = location.pathname;
            return;
        }
        
        const transition = findMatchingTransition(previousRoute.current, location.pathname);
        const matchingConfig = videoConfigs.find(v => 
            v.route.endsWith('/*') 
                ? location.pathname.startsWith(v.route.slice(0, -2))
                : v.route === location.pathname
        );
        
        const newBackground = matchingConfig?.src || videoConfigs[0]?.src;
        performTransition(newBackground, transition);
        previousRoute.current = location.pathname;
    }, [location.pathname, performTransition, getTransitionState]);

    return {
        videoState,
        contentVisible,
        handleNavigate: useCallback(async (to) => {
            debug(`Navigating to ${to} from ${location.pathname}`);
            
            try {
                if (pendingNavigation.current === to) {
                    debug(`Navigation to ${to} already pending, ignoring`);
                    return;
                }
                
                pendingNavigation.current = to;
                
                if (to === '/') {
                    sessionStorage.removeItem('animationCompleted');
                    sessionStorage.removeItem('lastDisplayedCount');
                    sessionStorage.setItem('overlayVisible', 'false');
                }
                
                // Special case for landing page to passive screen transition
                if (location.pathname === '/' && to === '/space') {
                    debug(`Special case: Landing to passive screen transition`);
                    
                    if (!acquireTransitionLock(instanceIdRef.current)) {
                        debug(`Could not acquire transition lock for landing to passive transition`);
                        navigate(to);
                        pendingNavigation.current = null;
                        return;
                    }
                    
                    setSeamlessTransition(instanceIdRef.current, true);
                    setIsPlaying(true);
                    
                    const passiveScreenConfig = videoConfigs.find(v => v.route === '/space');
                    if (passiveScreenConfig) {
                        setVideoState(prev => ({
                            ...prev,
                            background: passiveScreenConfig.src,
                            shouldReload: false
                        }));
                    }
                    
                    navigate(to);
                    pendingNavigation.current = null;
                    
                    setTimeout(() => {
                        releaseTransitionLock(instanceIdRef.current);
                    }, 2000);
                    
                    return;
                }
                
                const transition = findMatchingTransition(location.pathname, to);
                const matchingConfig = videoConfigs.find(v => 
                    v.route.endsWith('/*') 
                        ? to.startsWith(v.route.slice(0, -2))
                        : v.route === to
                );
                
                const newBackground = matchingConfig?.src || videoConfigs[0]?.src;
                await performTransition(newBackground, transition);
                
                navigate(to);
            } catch (error) {
                debug(`Navigation error:`, error);
                navigate(to);
            } finally {
                pendingNavigation.current = null;
            }
        }, [location.pathname, navigate, performTransition, acquireTransitionLock, releaseTransitionLock, setSeamlessTransition]),
        isPlaying,
        setIsPlaying
    };
};

// Helper function to find matching transition
const findMatchingTransition = (fromPath, toPath) => {
    const normalizedFromPath = fromPath.replace(/\/$/, '');
    const normalizedToPath = toPath.replace(/\/$/, '');
    
    return videoTransitions.find(t => {
        const normalizedFromRoute = t.from.replace(/\/$/, '');
        const normalizedToRoute = t.to.replace(/\/$/, '');
        
        const toMatches = normalizedToRoute.endsWith('/*') 
            ? normalizedToPath.startsWith(normalizedToRoute.slice(0, -2))
            : normalizedToRoute === normalizedToPath;
            
        return normalizedFromRoute === normalizedFromPath && toMatches;
    });
}; 